/*
 * Klaviyo onsite events for Murale:
 *   - "Added to Wishlist" / "Removed from Wishlist" (theme wishlist in theme.js, stored in localStorage)
 *   - "Requested Preview" (clicks on WhatsApp "digitaal voorbeeld" links and the Klaviyo preview pop-up)
 *
 * Klaviyo only records onsite events for identified browsers. Events from unidentified visitors are
 * kept in localStorage (max 10, max 24 hours) and sent once Klaviyo identifies the browser.
 * Nothing is sent or stored without analytics and marketing consent, the same consent Klaviyo
 * itself waits for before it tracks "Viewed Product".
 */
(function () {
  'use strict';

  var config = window.MuraleKlaviyoConfig || {};
  var WHATSAPP_NUMBER = '31614198973';
  var WISHLIST_KEY = 'localWishlist';
  var QUEUE_KEY = 'muraleKlaviyoQueue';
  var QUEUE_MAX = 10;
  var QUEUE_TTL = 24 * 60 * 60 * 1000;
  var NAVIGATION_DELAY = 300;
  var DUPLICATE_WINDOW = 1500;

  var productCache = {};
  var lastPreview = 0;

  function noop() {}

  // ---------------------------------------------------------------------------
  // Consent and identification

  // 'granted', 'denied' or 'unknown' (Shopify's consent API is not loaded).
  function consentState() {
    var privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (!privacy || typeof privacy.marketingAllowed !== 'function' || typeof privacy.analyticsProcessingAllowed !== 'function') {
      return 'unknown';
    }
    return privacy.marketingAllowed() && privacy.analyticsProcessingAllowed() ? 'granted' : 'denied';
  }

  // Resolves false on timeout: klaviyo.isIdentified() never settles while consent is missing.
  function isIdentified(timeout) {
    return new Promise(function (resolve) {
      var timer = setTimeout(function () { resolve(false); }, timeout);
      try {
        Promise.resolve(window.klaviyo.isIdentified()).then(function (identified) {
          clearTimeout(timer);
          resolve(identified === true);
        }, function () {
          clearTimeout(timer);
          resolve(false);
        });
      } catch (error) {
        clearTimeout(timer);
        resolve(false);
      }
    });
  }

  function track(name, properties) {
    try {
      return Promise.resolve(window.klaviyo.track(name, properties)).catch(noop);
    } catch (error) {
      return Promise.resolve();
    }
  }

  // ---------------------------------------------------------------------------
  // Queue for events that wait for identification

  function readQueue() {
    try {
      var queue = JSON.parse(localStorage.getItem(QUEUE_KEY)) || [];
      var cutoff = Date.now() - QUEUE_TTL;
      return queue.filter(function (item) { return item && item.time > cutoff; });
    } catch (error) {
      return [];
    }
  }

  function writeQueue(queue) {
    try {
      if (queue.length) {
        localStorage.setItem(QUEUE_KEY, JSON.stringify(queue.slice(-QUEUE_MAX)));
      } else {
        localStorage.removeItem(QUEUE_KEY);
      }
    } catch (error) {}
  }

  function flush(timeout) {
    if (!window.klaviyo || consentState() !== 'granted' || !readQueue().length) return Promise.resolve();

    return isIdentified(timeout || 1500).then(function (identified) {
      if (!identified) return;
      var queue = readQueue();
      writeQueue([]);
      return Promise.all(queue.map(function (item) { return track(item.event, item.properties); }));
    });
  }

  function record(name, properties) {
    if (!window.klaviyo) return Promise.resolve();

    var consent = consentState();
    if (consent === 'denied') return Promise.resolve();

    if (consent === 'unknown') {
      // Without Shopify's consent API, only send when Klaviyo itself is tracking; store nothing.
      return isIdentified(1500).then(function (identified) {
        if (identified) return track(name, properties);
      });
    }

    var queue = readQueue();
    queue.push({ event: name, properties: properties, time: Date.now() });
    writeQueue(queue);
    return flush();
  }

  // ---------------------------------------------------------------------------
  // Product data

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function parseJSON(text) {
    try {
      return JSON.parse(text);
    } catch (error) {
      return null;
    }
  }

  function pageProduct() {
    var element = document.getElementById('MuraleKlaviyoProduct');
    return element ? parseJSON(element.textContent) : null;
  }

  function getProduct(handle) {
    var current = pageProduct();
    if (current && current.handle === handle) return Promise.resolve(current);
    if (productCache[handle]) return productCache[handle];

    var url = root() + 'products/' + encodeURIComponent(handle) + '?section_id=klaviyo-product-data';
    productCache[handle] = fetch(url, { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then(function (html) {
        var element = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-klaviyo-product]');
        var product = element && parseJSON(element.textContent);
        if (!product) throw new Error('No product data');
        return product;
      })
      .catch(function () {
        delete productCache[handle];
        return null;
      });
    return productCache[handle];
  }

  function money(cents) {
    return Math.round(Number(cents)) / 100;
  }

  function selectedVariantId() {
    var field = document.querySelector('select[id^="ProductSelect-"], form[action*="/cart/add"] [name="id"]');
    if (field && field.value) return String(field.value);
    var match = window.location.search.match(/[?&]variant=(\d+)/);
    return match ? match[1] : null;
  }

  // Properties shared by all events that describe a product.
  function productProperties(product, variantId) {
    var variant = null;
    if (variantId) {
      variant = (product.variants || []).filter(function (item) { return item.id === String(variantId); })[0] || null;
    }
    var priceCents = variant ? variant.price : product.price;
    var compareCents = variant ? variant.compare_at_price : product.compare_at_price;
    var properties = {
      ProductID: product.id,
      ProductName: product.title,
      Handle: product.handle,
      URL: product.url,
      Price: money(priceCents),
      Brand: product.vendor,
      Categories: product.collections || []
    };

    if (variant) properties.VariantID = variant.id;
    if (product.image) properties.ImageURL = product.image;
    if (compareCents && compareCents > priceCents) properties.CompareAtPrice = money(compareCents);
    return properties;
  }

  // ---------------------------------------------------------------------------
  // Wishlist

  function readWishlist() {
    try {
      return (JSON.parse(localStorage.getItem(WISHLIST_KEY)) || []).map(String);
    } catch (error) {
      return [];
    }
  }

  function wishlistSource(button) {
    if (button.closest('#jsQuickview')) return 'quick_view';
    if (config.isWishlistPage) return 'wishlist_page';
    if (button.closest('.product-card') || button.closest('.product-card__overlay')) return 'collection_card';
    if (config.pageType === 'product') return 'product_page';
    return 'collection_card';
  }

  function onWishlistAdd(handle, source, variantId, count) {
    getProduct(handle).then(function (product) {
      if (!product) return;
      var properties = productProperties(product, variantId);
      properties.Source = source;
      properties.WishlistCount = count;
      properties.$value = properties.Price;
      return record('Added to Wishlist', properties);
    }).catch(noop);
  }

  function onWishlistRemove(handle, name, count) {
    getProduct(handle).then(function (product) {
      return record('Removed from Wishlist', {
        ProductID: product ? product.id : undefined,
        ProductName: product ? product.title : name,
        Handle: handle,
        WishlistCount: count
      });
    }).catch(noop);
  }

  // Capture phase: runs before the theme's jQuery handler, so the list still has its old state here.
  // The theme handler then updates localStorage synchronously; compare after it has run.
  document.addEventListener('click', function (event) {
    if (!event.target || !event.target.closest) return;

    var addButton = event.target.closest('.js-btn-wishlist');
    if (addButton) {
      var handle = String(addButton.getAttribute('data-handle') || '');
      // Clicking a product that is already on the list only opens the wishlist page.
      if (!handle || readWishlist().indexOf(handle) !== -1) return;

      var source = wishlistSource(addButton);
      var variantId = source === 'product_page' ? selectedVariantId() : null;
      setTimeout(function () {
        var list = readWishlist();
        if (list.indexOf(handle) !== -1) onWishlistAdd(handle, source, variantId, list.length);
      }, 0);
      return;
    }

    var removeButton = event.target.closest('.js-remove-wishlist');
    if (removeButton) {
      var removedHandle = String(removeButton.getAttribute('data-handle') || '');
      if (!removedHandle || readWishlist().indexOf(removedHandle) === -1) return;

      var item = removeButton.closest('.js-wishlist-item');
      var title = item && item.querySelector('.title-product');
      var name = title ? title.textContent.trim() : undefined;
      setTimeout(function () {
        var list = readWishlist();
        if (list.indexOf(removedHandle) === -1) onWishlistRemove(removedHandle, name, list.length);
      }, 0);
    }
  }, true);

  // ---------------------------------------------------------------------------
  // WhatsApp preview requests

  function whatsappLinkText(href) {
    var url;
    try {
      url = new URL(href, window.location.href);
    } catch (error) {
      return null;
    }

    var host = url.hostname.replace(/^www\./, '');
    var number = null;
    if (host === 'wa.me') {
      number = url.pathname.replace(/\D/g, '');
    } else if (host === 'api.whatsapp.com' || host === 'whatsapp.com' || host === 'web.whatsapp.com') {
      number = (url.searchParams.get('phone') || '').replace(/\D/g, '');
    }
    if (number !== WHATSAPP_NUMBER) return null;
    return url.searchParams.get('text') || '';
  }

  function previewSource(link) {
    var explicit = link.closest('[data-preview-source]');
    if (explicit) return explicit.getAttribute('data-preview-source');
    if (link.closest('footer, .shopify-section-group-footer-group')) return 'footer';
    if (link.closest('header, .shopify-section-group-header-group')) return 'header';
    if (config.pageType === 'product') return 'product_page';
    return 'page_content';
  }

  function previewProperties(source) {
    var properties = {
      Source: source,
      PageType: config.pageType || '',
      PageURL: window.location.href
    };

    var product = config.pageType === 'product' ? pageProduct() : null;
    if (product) {
      var productData = productProperties(product, selectedVariantId());
      properties.ProductID = productData.ProductID;
      properties.ProductName = productData.ProductName;
      properties.URL = productData.URL;
      properties.Price = productData.Price;
      if (productData.ImageURL) properties.ImageURL = productData.ImageURL;
    }
    return properties;
  }

  function recordPreview(source) {
    var now = Date.now();
    if (now - lastPreview < DUPLICATE_WINDOW) return Promise.resolve();
    lastPreview = now;
    return record('Requested Preview', previewProperties(source));
  }

  document.addEventListener('click', function (event) {
    if (!event.target || !event.target.closest) return;

    var link = event.target.closest('a[href]');
    if (!link) return;

    var text = whatsappLinkText(link.getAttribute('href'));
    if (text === null) return;
    // Only preview links count (tagged, or "voorbeeld" in the prefill or link text); plain contact links do not.
    if (!link.closest('[data-preview-source]') && !/voorbeeld/i.test(text + ' ' + link.textContent)) return;

    var sent = recordPreview(previewSource(link));

    var opensElsewhere = (link.target && link.target !== '_self') || event.button !== 0 ||
      event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.hasAttribute('download');
    if (opensElsewhere || event.defaultPrevented) return;

    // Same-tab link: give the event up to NAVIGATION_DELAY ms before leaving the page.
    event.preventDefault();
    var href = link.href;
    var navigated = false;
    function go() {
      if (navigated) return;
      navigated = true;
      window.location.href = href;
    }
    sent.then(go, go);
    setTimeout(go, NAVIGATION_DELAY);
  });

  // ---------------------------------------------------------------------------
  // Klaviyo form events

  window.addEventListener('klaviyoForms', function (event) {
    var detail = event.detail || {};

    // The preview pop-up's button redirects to WhatsApp; Klaviyo renders it, so there is no link to click.
    if (detail.type === 'redirectedToUrl' && detail.formId === config.previewFormId) {
      recordPreview('klaviyo_popup');
    }

    // A form submission identifies the visitor: send what was waiting.
    if (detail.type === 'submit') {
      [1000, 3000, 6000].forEach(function (delay) {
        setTimeout(function () { flush(); }, delay);
      });
    }
  });

  // ---------------------------------------------------------------------------
  // For other theme scripts (the wall preview): same consent, queue and product properties.

  window.MuraleKlaviyo = {
    trackProduct: function (name, variantId, extra) {
      var product = pageProduct();
      if (!product) return Promise.resolve();
      var properties = productProperties(product, variantId);
      properties.$value = properties.Price;
      Object.keys(extra || {}).forEach(function (key) { properties[key] = extra[key]; });
      return record(name, properties);
    }
  };

  document.addEventListener('visitorConsentCollected', function () {
    flush(5000);
  });

  // Visitors who come back identified (email click, account) get their queued events now.
  flush(10000);
})();
